import {
  Book,
  ClipboardList,
  FileText,
  Globe,
  Image,
  LayoutTemplate,
  Mic,
  Palette,
  SquarePen,
} from "lucide-react"

import { PanelCard, PanelCardHead, PanelCardBody } from "./PanelCard"
import AiConsent from "./brief/AiConsent"
import FactGrid from "./brief/FactGrid"
import FileRows from "./brief/FileRows"
import LongText, { Eyebrow, LongTexts } from "./brief/LongText"
import SensitiveContent from "./brief/SensitiveContent"
import VisualPicks from "./brief/VisualPicks"
import { BRIEF_COPY } from "./brief/copy"
import { cn } from "@/lib/utils"

const TYPE_ICON = {
  manuscript: Book,
  editing: SquarePen,
  formatting: LayoutTemplate,
  cover: Image,
  illustration: Palette,
  narration: Mic,
  seo: Globe,
}

// An aside card: an eyebrow over its content, no head bar.
function AsideCard({ title, className, children }) {
  return (
    <PanelCard className={className}>
      <PanelCardBody>
        <Eyebrow className="mb-3">{title}</Eyebrow>
        {children}
      </PanelCardBody>
    </PanelCard>
  )
}

function TimelineRow({ term, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border py-[11px] first:pt-0 last:border-b-0 last:pb-0">
      <dt className="text-[13px] font-semibold text-ink-3">{term}</dt>
      <dd className="text-right text-[13.5px] whitespace-nowrap text-foreground">{children}</dd>
    </div>
  )
}

// Order Details tab — the customer's whole brief (design/spec/pm/
// order-details.html): what they asked for first, then the answers particular
// to the order type, their visual picks and the order's common facts, with the
// files, the AI consent, the sensitive-content answers and the timeline
// alongside. Read-only apart from the requirements-file upload.
//
// The server (OrderBriefPayload) sends only the answers the order has, so a
// card with nothing to show is not rendered. Below 1081 the two columns
// become one and the cards interleave by `order`, in the design's priority.
export default function DetailsPanel({ order }) {
  const { brief, revisions = {} } = order
  if (!brief) return null

  const TypeIcon = TYPE_ICON[brief.type] ?? FileText
  const hasTypeCard = brief.typeFacts.length > 0 || brief.typeNotes.length > 0
  const column = "flex min-w-0 flex-col gap-[18px] max-[1080px]:contents"

  return (
    <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start gap-[18px] max-[1080px]:flex max-[1080px]:flex-col max-[1080px]:items-stretch">
      <div className={column}>
        {(brief.requirements || brief.notes.length > 0) && (
          <PanelCard className="order-1">
            <PanelCardHead Icon={Book} title={BRIEF_COPY.briefTitle} />
            <PanelCardBody className="flex flex-col gap-[18px] [&>*+*]:border-t [&>*+*]:border-border [&>*+*]:pt-[18px]">
              {brief.requirements && <LongText label={BRIEF_COPY.requirements} text={brief.requirements} lines={9} main />}
              {brief.notes.map((note) => (
                <LongText key={note.label} label={note.label} text={note.text} />
              ))}
            </PanelCardBody>
          </PanelCard>
        )}

        {hasTypeCard && (
          <PanelCard className="order-2">
            <PanelCardHead Icon={TypeIcon} title={brief.typeLabel} />
            <PanelCardBody>
              <FactGrid groups={brief.typeFacts} />
              <LongTexts
                items={brief.typeNotes}
                className={cn(brief.typeFacts.length > 0 && "mt-[18px] border-t border-border pt-[18px]")}
              />
            </PanelCardBody>
          </PanelCard>
        )}

        {brief.visual && (
          <PanelCard className="order-3">
            <PanelCardHead Icon={Palette} title={BRIEF_COPY.visualTitle} />
            <PanelCardBody>
              <VisualPicks visual={brief.visual} />
            </PanelCardBody>
          </PanelCard>
        )}

        <PanelCard className="order-7">
          <PanelCardHead Icon={ClipboardList} title={BRIEF_COPY.detailsTitle} />
          <PanelCardBody>
            <FactGrid groups={brief.common} />
          </PanelCardBody>
        </PanelCard>
      </div>

      <div className={column}>
        {brief.files.length > 0 && (
          <AsideCard title={BRIEF_COPY.filesTitle} className="order-4">
            <FileRows files={brief.files} orderId={order.id} />
          </AsideCard>
        )}

        {brief.aiConsent && (
          <AsideCard title={BRIEF_COPY.aiTitle} className="order-5">
            <AiConsent consent={brief.aiConsent} />
          </AsideCard>
        )}

        {brief.sensitive && (
          <AsideCard title={BRIEF_COPY.sensitiveTitle} className="order-6">
            <SensitiveContent content={brief.sensitive} />
          </AsideCard>
        )}

        <AsideCard title={BRIEF_COPY.timelineTitle} className="order-8">
          <dl>
            {brief.timeline.start && <TimelineRow term={BRIEF_COPY.startDate}>{brief.timeline.start}</TimelineRow>}
            <TimelineRow term={BRIEF_COPY.expectedDelivery}>{brief.timeline.due ?? BRIEF_COPY.toBeConfirmed}</TimelineRow>
            {revisions.max != null && (
              <TimelineRow term={BRIEF_COPY.revisionsIncluded}>{`${revisions.max} rounds`}</TimelineRow>
            )}
          </dl>
        </AsideCard>
      </div>
    </div>
  )
}
